import React from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../context/ThemeContext";

interface ThemeToggleProps {
  variant?: "icon" | "button";
  className?: string;
}

const ThemeToggle: React.FC<ThemeToggleProps> = ({
  variant = "icon",
  className = "",
}) => {
  const { theme, toggleTheme } = useTheme();
  const { t } = useTranslation();
  const isDark = theme === "dark";

  if (variant === "icon") {
    return (
      <div className={`theme-toggle-container ${className}`}>
        <button
          onClick={toggleTheme}
          className="theme-toggle-switch"
          title={
            isDark ? t("switch_to_light_theme") : t("switch_to_dark_theme")
          }
          aria-label={
            isDark ? t("switch_to_light_theme") : t("switch_to_dark_theme")
          }
          style={{
            position: "relative",
            width: "60px",
            height: "32px",
            border: "none",
            borderRadius: "16px",
            background: isDark
              ? "linear-gradient(135deg, #1a202c 0%, #2d3748 100%)"
              : "linear-gradient(135deg, #e2e8f0 0%, #cbd5e0 100%)",
            cursor: "pointer",
            transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
            boxShadow: isDark
              ? "inset 0 2px 4px rgba(0, 0, 0, 0.3), 0 2px 8px rgba(0, 0, 0, 0.2)"
              : "inset 0 2px 4px rgba(0, 0, 0, 0.1), 0 2px 8px rgba(0, 0, 0, 0.1)",
            outline: "none",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "scale(1.05)";
            e.currentTarget.style.boxShadow = isDark
              ? "inset 0 2px 4px rgba(0, 0, 0, 0.4), 0 4px 12px rgba(0, 0, 0, 0.3)"
              : "inset 0 2px 4px rgba(0, 0, 0, 0.15), 0 4px 12px rgba(0, 0, 0, 0.15)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "scale(1)";
            e.currentTarget.style.boxShadow = isDark
              ? "inset 0 2px 4px rgba(0, 0, 0, 0.3), 0 2px 8px rgba(0, 0, 0, 0.2)"
              : "inset 0 2px 4px rgba(0, 0, 0, 0.1), 0 2px 8px rgba(0, 0, 0, 0.1)";
          }}
        >
          {/* Track Icons */}
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "8px",
              transform: "translateY(-50%)",
              fontSize: "12px",
              color: isDark ? "#4a5568" : "#a0aec0",
              transition: "all 0.3s ease",
              opacity: isDark ? 0.5 : 1,
            }}
          >
            <i className="fas fa-sun"></i>
          </div>

          <div
            style={{
              position: "absolute",
              top: "50%",
              right: "8px",
              transform: "translateY(-50%)",
              fontSize: "12px",
              color: isDark ? "#e2e8f0" : "#4a5568",
              transition: "all 0.3s ease",
              opacity: isDark ? 1 : 0.5,
            }}
          >
            <i className="fas fa-moon"></i>
          </div>

          {/* Switch Handle */}
          <div
            style={{
              position: "absolute",
              top: "3px",
              left: isDark ? "31px" : "3px",
              width: "26px",
              height: "26px",
              borderRadius: "50%",
              background: isDark
                ? "linear-gradient(135deg, #4a5568 0%, #2d3748 100%)"
                : "linear-gradient(135deg, #ffffff 0%, #f7fafc 100%)",
              boxShadow: isDark
                ? "0 2px 6px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.1)"
                : "0 2px 6px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.8)",
              transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {/* Active Icon in Handle */}
            <i
              className={isDark ? "fas fa-moon" : "fas fa-sun"}
              style={{
                fontSize: "12px",
                color: isDark ? "#ffd700" : "#f6ad55",
                filter: isDark
                  ? "drop-shadow(0 0 4px rgba(255, 215, 0, 0.6))"
                  : "drop-shadow(0 0 4px rgba(246, 173, 85, 0.6))",
                transition: "all 0.3s ease",
              }}
            />
          </div>

          {/* Glow Effect */}
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: isDark ? "44px" : "16px",
              transform: "translate(-50%, -50%)",
              width: "20px",
              height: "20px",
              borderRadius: "50%",
              background: isDark
                ? "radial-gradient(circle, rgba(255, 215, 0, 0.3) 0%, transparent 70%)"
                : "radial-gradient(circle, rgba(246, 173, 85, 0.3) 0%, transparent 70%)",
              transition: "all 0.4s ease",
              pointerEvents: "none",
            }}
          />
        </button>
      </div>
    );
  }

  // Button variant with switch
  return (
    <div className={`d-flex align-items-center gap-3 ${className}`}>
      <div
        style={{
          position: "relative",
          width: "50px",
          height: "28px",
        }}
      >
        <button
          onClick={toggleTheme}
          style={{
            position: "relative",
            width: "100%",
            height: "100%",
            border: "none",
            borderRadius: "14px",
            background: isDark
              ? "linear-gradient(135deg, #1a202c 0%, #2d3748 100%)"
              : "linear-gradient(135deg, #e2e8f0 0%, #cbd5e0 100%)",
            cursor: "pointer",
            transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
            boxShadow: isDark
              ? "inset 0 2px 4px rgba(0, 0, 0, 0.3)"
              : "inset 0 2px 4px rgba(0, 0, 0, 0.1)",
            outline: "none",
          }}
        >
          {/* Mini Switch Handle */}
          <div
            style={{
              position: "absolute",
              top: "2px",
              left: isDark ? "24px" : "2px",
              width: "22px",
              height: "22px",
              borderRadius: "50%",
              background: isDark
                ? "linear-gradient(135deg, #4a5568 0%, #2d3748 100%)"
                : "linear-gradient(135deg, #ffffff 0%, #f7fafc 100%)",
              boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
              transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <i
              className={isDark ? "fas fa-moon" : "fas fa-sun"}
              style={{
                fontSize: "10px",
                color: isDark ? "#ffd700" : "#f6ad55",
              }}
            />
          </div>
        </button>
      </div>

      <span
        style={{
          fontWeight: "500",
          fontSize: "14px",
          color: isDark ? "#e2e8f0" : "#2d3748",
          transition: "color 0.3s ease",
        }}
      >
        {isDark ? t("dark_mode") : t("light_mode")}
      </span>
    </div>
  );
};

export default ThemeToggle;
